/** @jsxImportSource @emotion/react */
import {
  createStyles,
  Icon,
  IconName,
  Snackbar,
  Typography,
  Tooltip,
  useTheme,
} from '@quickbit/qb-design-system';
import { FormattedMessage, useIntl } from 'react-intl';
import { environment } from 'utils/environmentVariables';

interface Props {
  title?: string;
  onBack?: () => void;
  actions?: {
    icon: IconName;
    onClick: () => void;
  }[];
  breadcrumb?: string;
  progress?: number;
  isInModal?: boolean;
}

export const Header = ({
  title,
  onBack,
  breadcrumb,
  progress,
  actions,
  isInModal,
}: Props) => {
  const styles = useStyles();
  const { palette, spacing } = useTheme();
  const { formatMessage } = useIntl();

  if (progress && (progress < 0 || progress > 1)) {
    throw Error('Progress has to be 0 <= x <= 1');
  }

  return (
    <>
      <header css={[styles.header, !isInModal && styles.largeHeader]}>
        <div css={[styles.content, !isInModal && styles.largeContent]}>
          {onBack && (
            <div
              css={[styles.back, !isInModal && styles.largeBack]}
              onClick={() => {
                onBack && onBack();
              }}
            >
              <Icon name={'chevron'} />
            </div>
          )}
          {title && (
            <div css={[styles.title, !isInModal && styles.largeTitle]}>
              {breadcrumb && (
                <div
                  css={[
                    styles.breadcrumb,
                    !isInModal && styles.largeBreadcrumb,
                  ]}
                >
                  <div css={{ cursor: 'pointer' }} onClick={onBack}>
                    <Typography color={palette.grey.x300} variant="subtitle">
                      {breadcrumb}
                    </Typography>
                  </div>
                  <Icon name="chevron" size={spacing.xxs} />
                </div>
              )}
              <Typography variant="subtitle">{title}</Typography>
              {environment.READONLY && (
                <div>
                  <Snackbar
                    message={
                      <div
                        css={{
                          display: 'flex',
                          gap: '4px',
                          maxWidth: '200px',
                        }}
                      >
                        <Typography variant="bodySmallest" component={'div'}>
                          <div css={{ display: 'flex' }}>
                            <FormattedMessage id="readonly.header.banner" />
                            <Tooltip
                              title={formatMessage({
                                id: 'readonly.header.tooltip',
                              })}
                            >
                              <u>
                                <FormattedMessage id="readonly.header.readMore" />
                              </u>
                            </Tooltip>
                          </div>
                        </Typography>
                      </div>
                    }
                    variant="primary"
                    showAsBanner
                  />
                </div>
              )}
            </div>
          )}
          {actions &&
            actions.map(({ icon, onClick }) => (
              <div key={icon} css={styles.icon}>
                <div onClick={onClick}>
                  <Icon name={icon} />
                </div>
              </div>
            ))}
        </div>
      </header>
      {progress !== undefined && (
        <div css={styles.progressWidth}>
          <div css={styles.progressContainer}>
            <div
              css={[
                styles.progressBar,
                { width: `${progress * 100}%` },
                progress === 1 && { borderRadius: 0 },
              ]}
            />
          </div>
        </div>
      )}
    </>
  );
};

const useStyles = createStyles(({ spacing, palette, shape, breakpoints }) => ({
  header: {
    position: 'sticky',
    zIndex: 999,
    top: 0,
    display: 'flex',
    alignItems: 'center',
    padding: spacing.xxs,
    backgroundColor: palette.white,
    borderBottom: `1px solid ${palette.grey.x100}`,
    width: '100%',
    justifyContent: 'center',
  },
  largeHeader: {
    [breakpoints.laptopSmall]: {
      border: 'none',
      borderRadius: shape.borderRadius,
      display: 'block',
    },
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.xxxs,
    width: '100%',
    justifyContent: 'center',
    margin: '0 auto',
    '[aria-label="chevron"]': {
      svg: {
        stroke: palette.grey.x300,
        fill: 'none',
        rotate: '180deg',
      },
    },
  },
  largeTitle: {
    [breakpoints.laptopSmall]: {
      margin: '0',
      justifyContent: 'start',
    },
  },
  breadcrumb: {
    display: 'none',
  },
  largeBreadcrumb: {
    [breakpoints.laptopSmall]: {
      display: 'flex',
      alignItems: 'center',
      gap: spacing.xxxs,
    },
  },
  icon: {
    right: 0,
    position: 'absolute',
    cursor: 'pointer',
  },
  back: {
    position: 'absolute',
    left: 0,
    cursor: 'pointer',
    svg: { fill: 'none', stroke: palette.black },
  },
  largeBack: {
    [breakpoints.laptopSmall]: {
      display: 'none',
    },
  },
  content: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  largeContent: {
    [breakpoints.laptopSmall]: {
      padding: `calc(${spacing.xxxs}/2) 0`,
    },
  },
  progressBar: {
    background: palette.primary.default,
    height: spacing.xxxs,
    borderRadius: `0 ${shape.borderRadius} ${shape.borderRadius} 0`,
  },
  progressContainer: {
    background: palette.primary.x50,
    height: spacing.xxxs,
    width: '100%',
  },
  progressWidth: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
  },
}));
